import EnabledSwitch from '@app/components/common/EnabledSwitch';
import { FieldComponentProps, FieldGroup } from '@app/types/form.types';
import { TFunction } from 'i18next';
import { CaregiverSelectField, ClientSelectField, DoctorSelectField } from './SelectFieldWrappers';

// Use browser-provided timezones when available, otherwise fall back to a short list
const getTimezoneOptions = () => {
  const fallback = ['UTC', 'Europe/London', 'Europe/Athens', 'America/New_York', 'America/Los_Angeles'];

  try {
    const intlWithSupportedValues = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
    const supported =
      typeof intlWithSupportedValues.supportedValuesOf === 'function'
        ? intlWithSupportedValues.supportedValuesOf('timeZone')
        : fallback;

    const uniqueTimezones = Array.from(new Set(supported && supported.length ? supported : fallback));
    return uniqueTimezones
      .sort((a, b) => a.localeCompare(b))
      .map((tz) => ({
        label: tz,
        value: tz,
      }));
  } catch (error) {
    return fallback.map((tz) => ({ label: tz, value: tz }));
  }
};

export const timezoneOptions = getTimezoneOptions();

// Helper to cast component types for TypeScript compatibility
const castComponent = <TValue, Extra extends object = Record<string, unknown>>(
  Component: React.ComponentType<FieldComponentProps<TValue> & Extra>
) => Component as unknown as React.ComponentType<FieldComponentProps<unknown>>;

export const getUserFormConfig = (t: TFunction): FieldGroup[] => [
  {
    title: t('users-form-group-user-information'),
    fields: [
      {
        name: 'firstName',
        label: t('users-form-first-name'),
        type: 'text',
        width: 'half',
        rules: { required: t('users-validation-first-name-required') },
      },
      {
        name: 'lastName',
        label: t('users-form-last-name'),
        type: 'text',
        width: 'half',
        rules: { required: t('users-validation-last-name-required') },
      },
      {
        name: 'email',
        label: t('users-form-email'),
        type: 'email',
        width: 'half',
        rules: {
          required: t('users-validation-email-required'),
          pattern: {
            value: /^\S+@\S+$/i,
            message: t('users-validation-email-invalid'),
          },
        },
      },
      {
        name: 'enabled',
        label: t('users-form-enabled'),
        type: 'custom',
        width: 'half',
        component: castComponent(EnabledSwitch),
        componentProps: { label: t('users-form-enabled-label') },
      },
      {
        name: 'timezone',
        label: t('users-form-timezone'),
        type: 'select',
        width: 'half',
        rules: { required: t('users-validation-timezone-required') },
        options: timezoneOptions,
        componentProps: {
          showSearch: true,
          optionFilterProp: 'label',
          filterOption: (input: string, option?: { label?: string }) =>
            (option?.label || '').toLowerCase().includes(input.toLowerCase()),
        },
      },
      // {
      //   name: 'avatarUrl',
      //   label: 'Avatar URL',
      //   type: 'text',
      //   width: 'half',
      // },
      {
        name: 'roles',
        label: t('users-form-roles'),
        type: 'multiple-select',
        width: 'half',
        rules: { required: t('users-validation-roles-required') },
        options: [],
      },
      {
        name: 'caregiverId',
        label: t('users-form-caregiver'),
        type: 'custom',
        width: 'half',
        component: castComponent(CaregiverSelectField),
        hidden: true,
        // The label is rendered inside CaregiverSelectField so it can appear together
        // with the dropdown only once the prefilled caregiver's name has loaded.
        hideLabel: true,
      },
      {
        name: 'doctorId',
        label: t('users-form-doctor'),
        type: 'custom',
        width: 'half',
        component: castComponent(DoctorSelectField),
        hidden: true,
        // See caregiverId: label rendered inside DoctorSelectField.
        hideLabel: true,
      },
      {
        name: 'clientId',
        label: t('users-form-patient'),
        type: 'custom',
        width: 'half',
        component: castComponent(ClientSelectField),
        hidden: true,
        // See caregiverId: label rendered inside ClientSelectField.
        hideLabel: true,
      },
    ],
  },
];
